package fyi.moko.hotpl.di

import fyi.moko.hotpl.data.backup.LocalBackupManager
import fyi.moko.hotpl.domain.repository.PlaceRepository
import fyi.moko.hotpl.domain.usecase.AddVisitUseCase
import fyi.moko.hotpl.domain.usecase.AddVisitWithPhotosUseCase
import fyi.moko.hotpl.domain.usecase.CorrectPhotoUseCase
import fyi.moko.hotpl.domain.usecase.RotatePhotoUseCase
import fyi.moko.hotpl.domain.usecase.CreateInviteCodeUseCase
import fyi.moko.hotpl.domain.usecase.DeletePlaceUseCase
import fyi.moko.hotpl.domain.usecase.ObserveFriendsSharedPlacesUseCase
import fyi.moko.hotpl.domain.usecase.ExportBackupUseCase
import fyi.moko.hotpl.domain.usecase.GetPlaceUseCase
import fyi.moko.hotpl.domain.usecase.LinkAccountUseCase
import fyi.moko.hotpl.domain.usecase.LoadSampleDataUseCase
import fyi.moko.hotpl.domain.usecase.MergeDuplicatePlacesUseCase
import fyi.moko.hotpl.domain.usecase.ObserveAllVisitsUseCase
import fyi.moko.hotpl.domain.usecase.ObserveFriendsUseCase
import fyi.moko.hotpl.domain.usecase.ObservePlaceUseCase
import fyi.moko.hotpl.domain.usecase.ObservePlacesUseCase
import fyi.moko.hotpl.domain.usecase.ObserveFriendWishes
import fyi.moko.hotpl.domain.usecase.ObserveFriendWishesUseCase
import fyi.moko.hotpl.domain.usecase.ObserveVisitSummariesUseCase
import fyi.moko.hotpl.domain.usecase.ObserveVisitsUseCase
import fyi.moko.hotpl.domain.usecase.PrepareImportBackupUseCase
import fyi.moko.hotpl.domain.usecase.RedeemInviteCodeUseCase
import fyi.moko.hotpl.domain.usecase.RemoveFriendUseCase
import fyi.moko.hotpl.domain.usecase.RestoreBackupUseCase
import fyi.moko.hotpl.domain.usecase.RechargePlaceSelector
import fyi.moko.hotpl.domain.usecase.RevisitWindowCalculator
import fyi.moko.hotpl.domain.usecase.SavePlaceUseCase
import fyi.moko.hotpl.domain.usecase.SetPlaceVisibilityUseCase
import fyi.moko.hotpl.domain.usecase.ToggleLikedUseCase
import fyi.moko.hotpl.ui.account.AccountViewModel
import fyi.moko.hotpl.ui.changelog.ChangelogViewModel
import fyi.moko.hotpl.ui.detail.DetailViewModel
import fyi.moko.hotpl.ui.directory.DirectoryViewModel
import fyi.moko.hotpl.ui.friends.FriendsGateViewModel
import fyi.moko.hotpl.ui.friends.FriendsViewModel
import fyi.moko.hotpl.ui.home.HomeViewModel
import fyi.moko.hotpl.ui.photo.PhotoViewerViewModel
import fyi.moko.hotpl.ui.placeform.PlaceFormViewModel
import fyi.moko.hotpl.domain.service.AuthService
import fyi.moko.hotpl.domain.service.PlaceSearchProvider
import fyi.moko.hotpl.domain.service.RemotePlaceCatalog
import fyi.moko.hotpl.domain.service.SocialGraphService
import fyi.moko.hotpl.domain.service.SyncGate
import fyi.moko.hotpl.domain.service.UsageTracker
import fyi.moko.hotpl.data.service.BufferedUsageTracker
import fyi.moko.hotpl.data.service.FirebaseAuthService
import fyi.moko.hotpl.data.service.UsageEventSink
import fyi.moko.hotpl.data.network.FirestoreUsageEventSink
import fyi.moko.hotpl.ui.platformName
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlin.time.Clock
import fyi.moko.hotpl.data.service.SyncCoordinator
import fyi.moko.hotpl.data.service.FirebaseEmulatorConfig
import fyi.moko.hotpl.data.service.PlacesSearchConfig
import fyi.moko.hotpl.data.image.CloudBackedPlaceImageStore
import fyi.moko.hotpl.data.image.PhotoSyncStore
import fyi.moko.hotpl.data.image.PlaceImageStore
import fyi.moko.hotpl.data.network.FirebaseNetworkPlaceDataSource
import fyi.moko.hotpl.data.image.SharedPhotoMemoryCache
import fyi.moko.hotpl.data.network.FirebasePhotoBlobStore
import fyi.moko.hotpl.domain.service.PhotoBlobStore
import fyi.moko.hotpl.data.network.FirebaseSocialDataSource
import fyi.moko.hotpl.data.network.FirebaseYellowPageDataSource
import fyi.moko.hotpl.data.network.KtorPlaceSearchProvider
import fyi.moko.hotpl.data.network.NominatimPlaceSearchProvider
import fyi.moko.hotpl.data.network.NetworkPlaceDataSource
import fyi.moko.hotpl.data.network.NetworkYellowPageDataSource
import fyi.moko.hotpl.domain.repository.YellowPageRepository
import io.ktor.client.HttpClient
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import org.koin.core.module.dsl.viewModelOf
import org.koin.core.qualifier.named
import org.koin.dsl.module

/**
 * Platform-independent Koin module: repository, backup manager, use cases, and
 * ViewModels. All of these live in `shared/commonMain`. Platform modules (registered
 * by each app) supply the `HotplDatabase` and `LocationContextResolver`.
 */
/**
 * Koin qualifier for the platform's device-local [PlaceImageStore]. Platform modules bind
 * their file store under this name; [sharedModule] wraps it as the unqualified one.
 */
const val LOCAL_IMAGE_STORE = "local-image-store"

val sharedModule = module {
    single<AuthService> {
        val config = getOrNull<FirebaseEmulatorConfig>()
        FirebaseAuthService(config?.host)
    }
    single<NetworkPlaceDataSource> {
        val config = getOrNull<FirebaseEmulatorConfig>()
        FirebaseNetworkPlaceDataSource(config?.host)
    }
    single<NetworkYellowPageDataSource> {
        val config = getOrNull<FirebaseEmulatorConfig>()
        FirebaseYellowPageDataSource(config?.host)
    }
    single<SocialGraphService> {
        val config = getOrNull<FirebaseEmulatorConfig>()
        FirebaseSocialDataSource(config?.host)
    }
    // Photo bytes (feat/photo-cloud-sync). Platform modules register their file store under
    // the "local" qualifier; the unqualified PlaceImageStore everyone injects is the cloud-
    // backed wrapper, so every save queues an upload and a local miss fetches remotely.
    single { SharedPhotoMemoryCache() }
    single<PhotoBlobStore> {
        val config = getOrNull<FirebaseEmulatorConfig>()
        FirebasePhotoBlobStore(config?.host, get())
    }
    single<PhotoSyncStore> {
        CloudBackedPlaceImageStore(
            local = get(named(LOCAL_IMAGE_STORE)),
            blobs = get(),
            queue = get(),
            auth = get(),
        )
    }
    single<PlaceImageStore> { get<PhotoSyncStore>() }
    // Stage 5-2: Ktor-backed Google Places search client. A default HttpClient with
    // JSON content negotiation is registered here; platform modules can override it
    // with an engine-specific client if desired.
    single {
        HttpClient {
            install(ContentNegotiation) {
                json(
                    Json {
                        // encodeDefaults: kotlinx omits default-valued fields, which silently
                        // dropped rankPreference/maxResultCount/languageCode from every Places
                        // request body — the API then fell back to its own defaults (relevance
                        // ranking, 20 results, the device's locale), which is how Korean rows
                        // reached an English-only audience.
                        encodeDefaults = true
                        ignoreUnknownKeys = true
                        isLenient = true
                    },
                )
            }
        }
    }
    single<PlaceSearchProvider> {
        // The key is platform-injected (local.properties → BuildConfig on Android;
        // initKoin parameter on iOS/JS) — never hardcoded here (CLAUDE.md Secrets rule).
        // Nominatim (free, community-run public instance) only stands in when there's no
        // key — it fills what was previously an empty StubPlaceSearchProvider for
        // key-less builds. A configured key keeps using Google alone: Nominatim's usage
        // policy discourages autocomplete-volume traffic on the shared public instance
        // (operations.osmfoundation.org/policies/nominatim), and it has a documented
        // coverage gap versus Google Places, so it shouldn't be the default primary path
        // for users a paid key is already serving.
        val apiKey = getOrNull<PlacesSearchConfig>()?.apiKey
        if (apiKey.isNullOrBlank()) {
            NominatimPlaceSearchProvider(get())
        } else {
            KtorPlaceSearchProvider(get(), apiKey)
        }
    }
    // YellowPageRepository is bound in the platform modules because its Room-backed
    // implementation lives in nativeAndAndroidMain; JS provides a stub.
    // One gate shared by the coordinator (which obeys it) and LinkAccountUseCase (which holds it
    // during an account switch), so it must be a singleton.
    single { SyncGate() }
    // The Firestore data source doubles as the remote place-id index the account switch reads.
    single<RemotePlaceCatalog> { get<NetworkPlaceDataSource>() }
    single { SyncCoordinator(get(), get(), get(), get(), get(), get(), photoBlobStore = get(), photoFiles = get(), photoQueue = get()) }
    // Usage log (backlog 0-39). Process-wide scope on purpose: events must outlive whichever
    // screen sent them, and the tracker is the one thing that knows a session started.
    single<UsageEventSink> {
        val config = getOrNull<FirebaseEmulatorConfig>()
        FirestoreUsageEventSink(config?.host, CoroutineScope(SupervisorJob() + Dispatchers.Default))
    }
    single<UsageTracker> {
        BufferedUsageTracker(
            auth = get(),
            preference = get(),
            sink = get(),
            scope = CoroutineScope(SupervisorJob() + Dispatchers.Default),
            build = fyi.moko.hotpl.BuildInfo.GIT_SHA,
            platform = platformName,
            now = { Clock.System.now().toEpochMilliseconds() },
        )
    }
    factory { ObservePlacesUseCase(get()) }
    factory { ObserveVisitSummariesUseCase(get()) }
    factory<ObserveFriendWishes> { ObserveFriendWishesUseCase(get(), get()) }
    factory { ObservePlaceUseCase(get()) }
    factory { GetPlaceUseCase(get()) }
    factory { SavePlaceUseCase(get()) }
    factory { DeletePlaceUseCase(get(), get()) }
    factory { MergeDuplicatePlacesUseCase(get(), get()) }
    factory { ToggleLikedUseCase(get()) }
    factory { SetPlaceVisibilityUseCase(get()) }
    factory { ObserveVisitsUseCase(get()) }
    factory { ObserveAllVisitsUseCase(get()) }
    factory { AddVisitUseCase(get()) }
    factory { AddVisitWithPhotosUseCase(get(), get()) }
    factory { CorrectPhotoUseCase(get(), get()) }
    factory { RotatePhotoUseCase(get(), get(), get()) }
    factory { ExportBackupUseCase(get()) }
    factory { PrepareImportBackupUseCase(get()) }
    factory { RestoreBackupUseCase(get()) }
    factory { LoadSampleDataUseCase(get()) }
    factory { LinkAccountUseCase(get(), get(), get(), get()) }
    factory { CreateInviteCodeUseCase(get(), get()) }
    factory { RedeemInviteCodeUseCase(get(), get()) }
    factory { ObserveFriendsUseCase(get(), get()) }
    factory { RemoveFriendUseCase(get()) }
    factory { ObserveFriendsSharedPlacesUseCase(get(), get()) }
    single { RevisitWindowCalculator() }
    // Constructor defaults do not save us here: viewModelOf resolves every parameter
    // through Koin, so an unregistered default still crashes at first composition.
    single { RechargePlaceSelector() }

    // ViewModels — moved from androidApp/AppModule in Phase 4
    viewModelOf(::HomeViewModel)
    viewModelOf(::PlaceFormViewModel)
    viewModelOf(::DetailViewModel)
    viewModelOf(::PhotoViewerViewModel)
    viewModelOf(::AccountViewModel)
    viewModelOf(::DirectoryViewModel)
    viewModelOf(::FriendsViewModel)
    viewModelOf(::FriendsGateViewModel)
    viewModelOf(::ChangelogViewModel)
}